import { useCallback, useEffect, useMemo, useState } from "react";
import { Candidate, ElectionType, ListName } from "./types";

interface Props {
  selectedElectionType?: ElectionType;
  selectedListName?: ListName;
  listNames: ListName[];
  initialCandidatesList: Candidate[];
  showAllCandidates: boolean;
  showElectedCandidatesOnly: boolean;
}

export const useCandidateList = ({
  selectedElectionType,
  selectedListName,
  listNames,
  initialCandidatesList,
  showAllCandidates,
  showElectedCandidatesOnly,
}: Props) => {
  const [selectedElection, setSelectedElection] = useState<ElectionType | null>(
    selectedElectionType || null
  );
  const [selectedList, setSelectedList] = useState<ListName | null>(
    selectedListName || null
  );
  const [candidateList, setCandidateList] = useState<Candidate[]>(
    initialCandidatesList
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(showAllCandidates);

  const fetchElectedCandidates = useCallback(async () => {
    if (!showElectedCandidatesOnly) return;
    if (!selectedElection) return setCandidateList([]);

    try {
      setIsLoading(true);

      const response = await fetch(
        `/api/candidate-list/elected-candidates/${selectedElection?.value}`
      );

      if (!response.ok) throw new Error("Error fetching elected candidates");

      response.json().then(({ candidates }: { candidates: Candidate[] }) => {
        setCandidateList(candidates);
        setIsLoading(false);
        setIsOpen(showAllCandidates);
      });
    } catch (error) {
      setIsLoading(false);
      // Handle the error if needed
    }
  }, [selectedElection, showAllCandidates, showElectedCandidatesOnly]);

  const fetchCandidateList = useCallback(async () => {
    if (!selectedList) return setCandidateList([]);

    try {
      setIsLoading(true);

      const response = await fetch(
        `/api/candidate-list/candidates/${selectedList?.value}`
      );

      if (!response.ok) throw new Error("Error fetching candidates");

      response.json().then(({ candidates }: { candidates: Candidate[] }) => {
        setCandidateList(candidates);
        setIsLoading(false);
        setIsOpen(showAllCandidates);
      });
    } catch (error) {
      setIsLoading(false);
      // Handle the error if needed
    }
  }, [selectedList, showAllCandidates]);

  useEffect(() => {
    if (showElectedCandidatesOnly) {
      fetchElectedCandidates();
    } else {
      fetchCandidateList();
    }
  }, [fetchCandidateList, fetchElectedCandidates, showElectedCandidatesOnly]);

  const candidates = useMemo(() => {
    if (showElectedCandidatesOnly) {
      return candidateList;
    }

    return (isOpen ? candidateList : candidateList.slice(0, 4)).filter(
      (candidate) => !candidate.isSubstitute
    );
  }, [candidateList, isOpen, showElectedCandidatesOnly]);

  const substitutes = useMemo(
    () =>
      isOpen ? candidateList.filter((candidate) => candidate.isSubstitute) : [],
    [candidateList, isOpen]
  );

  const filteredListNames = useMemo(
    () =>
      selectedElection
        ? listNames.filter((listName) =>
            listName.value.includes(selectedElection.value)
          )
        : [],
    [listNames, selectedElection]
  );

  useEffect(() => {
    if (!selectedElection) return;

    filteredListNames.length === 1 && setSelectedList(filteredListNames[0]);
  }, [filteredListNames, selectedElection]);

  return {
    isOpen,
    isLoading,
    candidates,
    substitutes,
    selectedList,
    selectedElection,
    filteredListNames,
    setIsOpen,
    setSelectedList,
    setSelectedElection,
  };
};
